<script lang="ts">
  import { modalDialog } from "@/actions/modalDialog";
  import { get } from "svelte/store";
  import { untrack } from "svelte";
  import { startObservationPoll } from "@/utils/observation-poll";
  import { api, type MinecraftPlayer, type ServerPlayers, type ServerTelemetry } from "@/api";
  import { statusLabels } from "@/config";
  import { token as tokenStore } from "@/stores/session";
  import {
    consoleAgentOnline,
    consolePlayers,
    consoleTelemetry,
    consoleConnection,
    consoleServerId,
  } from "@/stores/console";
  import TextInput from "@/components/TextInput.svelte";
  import { PlayerSnapshotFence } from "@/utils/player-snapshot-fence";
  import { playersAreStale } from "@/utils/player-freshness";

  const {
    serverId,
    status,
    part,
  }: { serverId: string; status: string; part: "metrics" | "players" } = $props();

  // Parent REST rows may be replaced while these primitive values stay equal.
  // Cache them so lifecycle effects observe identity/state, not the row getter.
  const observationServerId = $derived(serverId);
  const observationStatus = $derived(status);
  const observationPart = $derived(part);

  let telemetry = $state<ServerTelemetry | null>(null);
  let players = $state<ServerPlayers | null>(null);
  let loading = $state(true);
  let error = $state("");
  let stale = $state(true);
  let kickTarget = $state<MinecraftPlayer | null>(null);
  let reason = $state("");
  let kickBusy = $state<string | null>(null);
  let actionError = $state("");
  let notice = $state("");
  let lifetime = 0;
  let loadSequence = 0;
  let telemetryCapturedAt: number | null = null;
  let playerFence = new PlayerSnapshotFence("");
  let freshnessTime = $state(Date.now());
  let poll: ReturnType<typeof startObservationPoll> | null = null;
  const healthySocket = $derived(
    $consoleConnection === "connected" && $consoleServerId === serverId,
  );

  const running = $derived(status === "running");
  const agentOffline = $derived($consoleAgentOnline === false);
  const metricsStale = $derived.by(() => {
    if (!telemetry) {
      return true;
    }
    const capturedAt = Date.parse(telemetry.capturedAt);
    const age = Math.max(freshnessTime, Date.now()) - capturedAt;
    const windowSec =
      Number.isFinite(telemetry.staleAfterSec) && telemetry.staleAfterSec > 0
        ? Math.min(120, telemetry.staleAfterSec)
        : 45;
    return !Number.isFinite(age) || age < 0 || age > windowSec * 1_000;
  });
  const dataStale = $derived.by(() => {
    const feedUnavailable = stale || agentOffline;
    if (feedUnavailable) {
      return true;
    }
    const metricsOutdated = part === "metrics" && metricsStale;
    return (
      metricsOutdated ||
      (part === "players" && playersAreStale(players, Math.max(freshnessTime, Date.now())))
    );
  });
  // A connected namespace alone does not prove this observation feed is live.
  // Let the initial snapshot settle before choosing its fallback cadence.
  const healthyFeed = $derived(healthySocket && (loading || !dataStale));
  const stateLabel = $derived(
    !running
      ? (statusLabels[status] ?? "Ожидаем состояние")
      : agentOffline
        ? "нет связи с оборудованием"
        : dataStale
          ? "устаревшие данные"
          : "в реальном времени",
  );

  function requireToken(): string {
    const value = get(tokenStore);
    if (!value) {
      throw new Error("нет активной сессии");
    }
    return value;
  }

  function formatMb(value: number | null): string {
    if (value === null) {
      return "—";
    }
    if (value >= 1024) {
      return `${(value / 1024).toFixed(1)} ГБ`;
    }
    return `${value.toLocaleString("ru-RU")} МБ`;
  }

  function formatPercent(value: number | null): string {
    return value === null ? "—" : `${value.toFixed(1)}%`;
  }

  async function load(signal?: AbortSignal): Promise<boolean> {
    const requestedServerId = serverId;
    const requestedPart = part;
    const requestedLifetime = lifetime;
    const requestedSequence = ++loadSequence;
    const isCurrent = () => {
      const requestStillActive = requestedSequence === loadSequence && !signal?.aborted;
      return (
        requestStillActive &&
        requestedLifetime === lifetime &&
        requestedServerId === serverId &&
        requestedPart === part
      );
    };
    if (!running) {
      loading = false;
      stale = true;
      return false;
    }
    try {
      const token = requireToken();
      if (requestedPart === "metrics") {
        const metrics = await api.telemetry(token, requestedServerId, signal);
        if (!isCurrent()) {
          return false;
        }
        const live = get(consoleTelemetry);
        if (live?.serverId === requestedServerId && acceptTelemetrySnapshot(live)) {
          telemetry = live;
          stale = false;
        }
        if (metrics.telemetry) {
          if (!acceptTelemetrySnapshot(metrics.telemetry)) {
            return true;
          }
          telemetry = metrics.telemetry;
        }
        stale = metrics.stale;
      } else {
        const p = await api.players(token, requestedServerId, signal);
        if (!isCurrent()) {
          return false;
        }
        const live = get(consolePlayers);
        if (live?.serverId === requestedServerId && playerFence.accept(live)) {
          players = live;
          stale = playersAreStale(live);
        }
        if (playerFence.accept(p)) {
          players = p;
          stale = playersAreStale(p);
        }
      }
      error = "";
      return true;
    } catch (requestError) {
      if (isCurrent() && (requestError as Error).name !== "AbortError") {
        error = (requestError as Error).message;
      }
      return false;
    } finally {
      if (isCurrent()) {
        loading = false;
      }
    }
  }

  function acceptTelemetrySnapshot(value: ServerTelemetry): boolean {
    const captured = Date.parse(value.capturedAt);
    if (
      value.serverId !== serverId ||
      !Number.isFinite(captured) ||
      (telemetryCapturedAt !== null && captured <= telemetryCapturedAt)
    ) {
      return false;
    }
    telemetryCapturedAt = captured;
    return true;
  }

  $effect(() => {
    const startedAt = Date.now();
    const startedMonotonic = performance.now();
    freshnessTime = startedAt;
    const interval = window.setInterval(() => {
      // A wall-clock correction must not rejuvenate retained observations or
      // stop their age from advancing while polling is unavailable.
      freshnessTime = Math.max(
        freshnessTime,
        Date.now(),
        startedAt + performance.now() - startedMonotonic,
      );
    }, 1_000);
    return () => window.clearInterval(interval);
  });

  $effect(() => {
    const currentServerId = observationServerId;
    void $tokenStore;
    telemetryCapturedAt = null;
    playerFence = new PlayerSnapshotFence(currentServerId);
    telemetry = null;
    players = null;
    stale = true;
    error = "";
  });

  $effect(() => {
    void observationServerId;
    void observationStatus;
    void $tokenStore;
    lifetime += 1;
    kickTarget = null;
    kickBusy = null;
    reason = "";
    actionError = "";
    notice = "";
    const controller = new AbortController();
    const fallbackMs = observationPart === "metrics" ? 7_000 : 10_000;
    loading = running;
    // Only view identity changes own cancellation; stream events and UI actions
    // update cadence without restarting an outstanding request or its fences.
    const ownedPoll = untrack(() =>
      startObservationPoll({
        request: () => load(controller.signal),
        fallbackMs,
        healthy: healthyFeed,
        connected: healthySocket,
        visible: running && document.visibilityState !== "hidden",
      }),
    );
    poll = ownedPoll;
    const visibilityChanged = () =>
      ownedPoll.update({
        healthy: healthyFeed,
        visible: running && document.visibilityState !== "hidden",
        connected: healthySocket,
      });
    document.addEventListener("visibilitychange", visibilityChanged);
    return () => {
      lifetime += 1;
      ownedPoll.stop();
      controller.abort();
      document.removeEventListener("visibilitychange", visibilityChanged);
      if (poll === ownedPoll) {
        poll = null;
      }
    };
  });

  $effect(() => {
    const healthy = healthyFeed;
    const connected = healthySocket;
    const visible = running && document.visibilityState !== "hidden";
    untrack(() => poll?.update({ healthy, visible, connected }));
  });

  $effect(() => {
    if (
      part === "metrics" &&
      $consoleTelemetry?.serverId === serverId &&
      acceptTelemetrySnapshot($consoleTelemetry)
    ) {
      telemetry = $consoleTelemetry;
      stale = false;
    }
    if (
      part === "players" &&
      $consolePlayers?.serverId === serverId &&
      playerFence.accept($consolePlayers)
    ) {
      players = $consolePlayers;
      stale = playersAreStale($consolePlayers);
    }
  });

  function openKick(player: MinecraftPlayer): void {
    actionError = "";
    notice = "";
    reason = "";
    kickTarget = player;
  }

  async function kick(): Promise<void> {
    if (!kickTarget || kickBusy) {
      return;
    }
    const player = kickTarget;
    const requestedServerId = serverId;
    const requestedLifetime = lifetime;
    const isCurrent = () => requestedLifetime === lifetime && requestedServerId === serverId;
    kickBusy = player.name;
    actionError = "";
    try {
      await api.kickPlayer(requireToken(), requestedServerId, player.name, reason);
      if (!isCurrent()) {
        return;
      }
      kickTarget = null;
      notice = `Игрок ${player.name} отключён`;
      poll?.refresh();
    } catch (requestError) {
      if (isCurrent()) {
        actionError = (requestError as Error).message;
      }
    } finally {
      if (isCurrent()) {
        kickBusy = null;
      }
    }
  }
</script>

{#if part === "metrics"}
  <section class="panel-card telemetry-card" aria-labelledby="metrics-title">
    <header class="panel-heading">
      <div>
        <p class="eyebrow">Мониторинг</p>
        <h2 id="metrics-title">Ресурсы</h2>
      </div>
      <span class="state-chip" class:warn={!running || dataStale}>{stateLabel}</span>
    </header>
    {#if error}<div class="card-error" role="alert">{error}</div>{/if}
    <div class="metric-grid">
      <article class="metric-tile">
        <span>Процессор</span>
        <strong>{formatPercent(telemetry?.cpuPercent ?? null)}</strong>
        <small>лимит {formatPercent(telemetry?.cpuLimitPercent ?? null)}</small>
      </article>
      <article class="metric-tile">
        <span>Оперативная память</span>
        <strong>{formatMb(telemetry?.memoryUsedMb ?? null)}</strong>
        <small>лимит {formatMb(telemetry?.memoryLimitMb ?? null)}</small>
      </article>
      <article class="metric-tile">
        <span>Диск</span>
        <strong>{formatMb(telemetry?.diskUsedMb ?? null)}</strong>
        <small
          >{telemetry?.diskLimitMb == null
            ? "лимит не задан"
            : `лимит ${formatMb(telemetry.diskLimitMb)}`}</small
        >
      </article>
      <article class="metric-tile">
        <span>Игроки</span>
        <strong>{telemetry?.playersOnline ?? "—"}</strong>
        <small
          >{telemetry?.playersMax != null
            ? `из ${telemetry.playersMax}`
            : "лимит неизвестен"}</small
        >
      </article>
    </div>
  </section>
{:else}
  <section class="panel-card telemetry-card" aria-labelledby="players-card-title">
    <header class="panel-heading">
      <div>
        <p class="eyebrow">Мониторинг</p>
        <h2 id="players-card-title">Игроки</h2>
      </div>
      {#if !running}
        <span class="state-chip warn">{statusLabels[status] ?? "Ожидаем состояние"}</span>
      {:else if dataStale}
        <span class="state-chip warn">{stateLabel}</span>
      {:else if players}
        <span class="state-chip"
          >{players.playersOnline ?? players.players.length}{players.playersMax != null
            ? ` / ${players.playersMax}`
            : ""}</span
        >
      {/if}
    </header>
    {#if error}<div class="card-error" role="alert">{error}</div>{/if}
    {#if notice}<div class="card-notice">{notice}</div>{/if}
    {#if actionError}<div class="card-error" role="alert">{actionError}</div>{/if}
    <div class="players-body">
      {#if loading}
        <div class="players-empty">Загружаем список игроков…</div>
      {:else if !running}
        <div class="players-empty">Список игроков будет доступен после запуска сервера.</div>
      {:else if dataStale && !players?.players.length}
        <div class="players-empty">Нет актуальных данных об игроках</div>
      {:else if !players?.players.length}
        <div class="players-empty">Игроков нет</div>
      {:else}
        {#if dataStale}
          <p class="players-empty">Последний известный список игроков</p>
        {/if}
        <div class="player-list">
          {#each players.players as player (player.name)}
            <div class="player-row">
              <div>
                <strong>{player.name}</strong>
                {#if player.latencyMs != null}<small>{player.latencyMs} ms</small>{/if}
              </div>
              <button
                type="button"
                disabled={kickBusy !== null || !running}
                onclick={() => openKick(player)}
              >
                Отключить
              </button>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </section>

  {#if kickTarget}
    <dialog
      class="kick-dialog"
      use:modalDialog={{
        onDismiss: () => {
          kickTarget = null;
        },
        canDismiss: () => kickBusy === null,
      }}
      aria-labelledby="kick-title"
    >
      <p class="eyebrow">Управление игроком</p>
      <h3 id="kick-title">Отключить {kickTarget.name}?</h3>
      {#if actionError}
        <div class="dialog-error" role="alert">{actionError}</div>
      {/if}
      <label for="kick-reason">Причина <span>необязательно</span></label>
      <TextInput
        id="kick-reason"
        bind:value={reason}
        maxlength={256}
        placeholder="Например: технические работы"
      />
      <div class="dialog-actions">
        <button
          type="button"
          class="ghost-action"
          disabled={kickBusy !== null}
          onclick={() => (kickTarget = null)}>Отмена</button
        >
        <button
          type="button"
          class="button"
          disabled={kickBusy !== null}
          onclick={() => void kick()}
        >
          {kickBusy ? "Отключаем…" : "Отключить"}
        </button>
      </div>
    </dialog>
  {/if}
{/if}

<style>
  .telemetry-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    min-width: 0;
    align-content: start;
  }

  .telemetry-card .panel-heading {
    flex-wrap: wrap;
  }

  .state-chip {
    color: var(--green);
    font: 500 0.62rem/1 var(--font-display);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: normal;
    line-height: 1.4;
  }

  .state-chip.warn {
    color: var(--amber);
  }

  .card-error,
  .card-notice {
    padding: 0.7rem 1.5rem;
    border-bottom: 1px solid var(--line);
    font-size: 0.72rem;
  }

  .card-error {
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.08);
  }

  .card-notice {
    color: var(--green);
    background: rgb(154 189 143 / 0.06);
  }

  .metric-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    margin: 1.5rem;
    border: 1px solid var(--line);
    background: var(--line);
  }

  .metric-tile {
    display: grid;
    gap: 0.4rem;
    padding: 1rem 1.1rem;
    background: var(--surface);
  }

  .metric-tile span {
    color: var(--dim);
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .metric-tile strong {
    font: 500 clamp(1.05rem, 2vw, 1.45rem)/1 var(--font-display);
  }

  .metric-tile small {
    color: var(--muted);
    font-size: 0.62rem;
  }

  .players-body {
    padding: 1.5rem;
  }

  .players-empty {
    padding: 2.5rem 0;
    color: var(--dim);
    font-size: 0.75rem;
    text-align: center;
  }

  .player-list {
    display: grid;
    gap: 1px;
    border: 1px solid var(--line);
    background: var(--line);
  }

  .player-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1.1rem;
    background: var(--surface);
  }

  .player-row div {
    display: grid;
    gap: 0.25rem;
  }

  .player-row strong {
    font-size: 0.8rem;
  }

  .player-row small {
    color: var(--dim);
    font-size: 0.65rem;
  }

  .player-row button {
    padding: 0.5rem 0.7rem;
    border: 1px solid var(--line-strong);
    color: var(--mars-light);
    background: transparent;
    font-size: 0.68rem;
  }

  .player-row button:hover:not(:disabled) {
    background: rgb(214 75 50 / 0.1);
  }

  .kick-dialog::backdrop {
    background: rgb(0 0 0 / 0.72);
  }

  .kick-dialog {
    width: min(calc(100% - 2rem), 30rem);
    max-height: calc(100dvh - 2rem);
    overflow-y: auto;
    margin: auto;
    color: var(--cream);
    display: grid;
    gap: 1rem;
    padding: 1.5rem;
    border: 1px solid var(--line-strong);
    background: var(--surface-raised);
    box-shadow: 0 24px 80px rgb(0 0 0 / 0.5);
  }

  .kick-dialog h3 {
    margin: 0;
    font: 500 1.25rem/1.25 var(--font-display);
  }

  .dialog-error {
    padding: 0.6rem 0.8rem;
    border: 1px solid rgb(214 75 50 / 0.35);
    background: rgb(214 75 50 / 0.1);
    color: var(--mars-light);
    font-size: 0.72rem;
  }

  .kick-dialog label {
    color: var(--muted);
    font-size: 0.7rem;
  }

  .kick-dialog label span {
    color: var(--dim);
  }

  .dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
  }

  .ghost-action {
    padding: 0.5rem 0.7rem;
    border: 1px solid var(--line-strong);
    color: var(--muted);
    background: transparent;
    font-size: 0.68rem;
  }

  .ghost-action:hover:not(:disabled) {
    color: var(--cream);
  }

  .dialog-actions .button {
    min-height: 2.7rem;
  }

  @media (max-width: 520px) {
    .dialog-actions {
      flex-direction: column-reverse;
    }

    .dialog-actions button {
      width: 100%;
    }
  }
</style>
